import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FocusEvent,
  type PointerEvent,
  type SVGProps,
} from 'react';
import {
  AnimatePresence,
  motion,
} from 'framer-motion';

import useSessionTimer from '../../hooks/useSessionTimer';
import useSessionModal from '../../hooks/useSessionModal';
import { formatSessionTimeCompact } from '../../utils/sessionTime';

import SessionModal from './SessionModal';

const HOVER_DELAY_MS = 1000;

/**
 * Reloj de arena SVG.
 */
const HourglassIcon = ({
  className,
  ...props
}: SVGProps<SVGSVGElement>) => {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      {/* Líneas superior e inferior */}
      <path
        d="M6 2H18M6 22H18"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />

      {/* Estructura del reloj */}
      <path
        d="
          M7.5 2V6.1
          C7.5 8.05 8.55 9.85 10.25 10.85
          L12 12
          L13.75 10.85
          C15.45 9.85 16.5 8.05 16.5 6.1
          V2
        "
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="
          M7.5 22V17.9
          C7.5 15.95 8.55 14.15 10.25 13.15
          L12 12
          L13.75 13.15
          C15.45 14.15 16.5 15.95 16.5 17.9
          V22
        "
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Arena */}
      <path
        d="M9.2 6H14.8L12 9.2L9.2 6Z"
        fill="currentColor"
      />

      <path
        d="M12 14.6L15 19H9L12 14.6Z"
        fill="currentColor"
      />
    </svg>
  );
};

/**
 * ⏳ SessionTimerDisplay
 * ------------------------------------------------------------
 * Botón flotante de sesión.
 *
 * Escritorio:
 * - Muestra únicamente el reloj de arena.
 * - Después de mantener el cursor, muestra el tiempo.
 *
 * Móvil:
 * - No aplica el efecto hover.
 * - Un toque abre directamente el modal.
 *
 * Teclado:
 * - Al recibir foco visible, muestra el tiempo.
 */
const SessionTimerDisplay = () => {
  const { timeLeft, openModal } = useSessionTimer();
  const { isModalVisible } = useSessionModal();

  const [isExpanded, setIsExpanded] = useState(false);

  const hoverTimeoutRef = useRef<
    ReturnType<typeof setTimeout> | null
  >(null);

  const safeTimeLeft = Math.max(timeLeft, 0);

  const formattedTime = formatSessionTimeCompact(safeTimeLeft);

  const colorClass = useMemo(() => {
    if (timeLeft <= 30) {
      return `
        bg-[#DC3545]
        text-white
        hover:bg-[#C53030]
      `;
    }

    if (timeLeft <= 120) {
      return `
        bg-[#F9C107]
        text-[#212B36]
        hover:bg-[#E0A800]
      `;
    }

    return `
      bg-primary
      text-white
      hover:bg-primary/90
    `;
  }, [timeLeft]);

  /**
   * Cancela la expansión pendiente.
   */
  const clearHoverTimeout = useCallback(() => {
    if (!hoverTimeoutRef.current) {
      return;
    }

    clearTimeout(hoverTimeoutRef.current);
    hoverTimeoutRef.current = null;
  }, []);

  /**
   * Contrae completamente el botón.
   */
  const collapseTimer = useCallback(() => {
    clearHoverTimeout();
    setIsExpanded(false);
  }, [clearHoverTimeout]);

  /**
   * Inicia la expansión únicamente para mouse o lápiz.
   *
   * Los dispositivos táctiles no deben activar hover.
   */
  const handlePointerEnter = (
    event: PointerEvent<HTMLButtonElement>,
  ) => {
    if (event.pointerType === 'touch') {
      return;
    }

    clearHoverTimeout();

    hoverTimeoutRef.current = setTimeout(() => {
      setIsExpanded(true);
      hoverTimeoutRef.current = null;
    }, HOVER_DELAY_MS);
  };

  /**
   * Contrae el botón cuando se retira el cursor.
   */
  const handlePointerLeave = () => {
    collapseTimer();
  };

  /**
   * En móvil evita que algún estado hover quede activo.
   */
  const handlePointerDown = (
    event: PointerEvent<HTMLButtonElement>,
  ) => {
    if (event.pointerType === 'touch') {
      collapseTimer();
    }
  };

  /**
   * Permite visualizar el tiempo al navegar con teclado.
   *
   * No se expande cuando el foco proviene de un clic.
   */
  const handleFocus = (
    event: FocusEvent<HTMLButtonElement>,
  ) => {
    if (event.currentTarget.matches(':focus-visible')) {
      clearHoverTimeout();
      setIsExpanded(true);
    }
  };

  /**
   * Contrae el botón antes de abrir el modal.
   */
  const handleOpenModal = () => {
    collapseTimer();
    openModal();
  };

  /**
   * Cada vez que el modal se abre o se cierra, se restablece
   * completamente el estado visual del botón.
   */
  useEffect(() => {
    collapseTimer();
  }, [isModalVisible, collapseTimer]);

  /**
   * Limpieza al desmontar el componente.
   */
  useEffect(() => {
    return () => {
      clearHoverTimeout();
    };
  }, [clearHoverTimeout]);

  return (
    <>
      {(isModalVisible || timeLeft <= 0) && (
        <SessionModal />
      )}

      {!isModalVisible && timeLeft > 0 && (
        <motion.button
          key="session-timer"
          type="button"
          layout
          initial={{
            opacity: 0,
            scale: 0.8,
          }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          whileHover={{
            scale: 1.06,
          }}
          whileTap={{
            scale: 0.94,
          }}
          transition={{
            layout: {
              duration: 0.25,
              ease: 'easeInOut',
            },
            opacity: {
              duration: 0.2,
            },
            scale: {
              duration: 0.2,
            },
          }}
          onPointerEnter={handlePointerEnter}
          onPointerLeave={handlePointerLeave}
          onPointerDown={handlePointerDown}
          onFocus={handleFocus}
          onBlur={collapseTimer}
          onClick={handleOpenModal}
          aria-label={`Tiempo restante de sesión: ${formattedTime}`}
          aria-haspopup="dialog"
          className={`
            fixed bottom-3 right-3 z-[9999]
            flex h-10 items-center justify-center
            overflow-hidden rounded-full
            shadow-lg outline-none
            transition-colors duration-300
            focus-visible:ring-2
            focus-visible:ring-primary
            focus-visible:ring-offset-2
            dark:focus-visible:ring-offset-boxdark
            sm:bottom-5 sm:right-5
            ${
              isExpanded
                ? 'gap-2 px-3.5'
                : 'w-10 px-0'
            }
            ${colorClass}
          `}
        >
          <motion.span
            className="
              flex shrink-0 items-center justify-center
            "
            animate={{
              rotate: [0, 0, 180, 180, 360],
            }}
            transition={{
              duration: 3,
              times: [0, 0.32, 0.5, 0.82, 1],
              ease: 'easeInOut',
              repeat: Infinity,
            }}
          >
            <HourglassIcon
              className="h-[18px] w-[18px]"
              aria-hidden="true"
            />
          </motion.span>

          <AnimatePresence initial={false}>
            {isExpanded && (
              <motion.span
                key="session-time"
                initial={{
                  opacity: 0,
                  width: 0,
                  x: -5,
                }}
                animate={{
                  opacity: 1,
                  width: 'auto',
                  x: 0,
                }}
                exit={{
                  opacity: 0,
                  width: 0,
                  x: -5,
                }}
                transition={{
                  duration: 0.2,
                  ease: 'easeInOut',
                }}
                className="
                  overflow-hidden whitespace-nowrap
                  text-xs font-semibold leading-none
                "
              >
                {formattedTime}
              </motion.span>
            )}
          </AnimatePresence>
        </motion.button>
      )}
    </>
  );
};

export default SessionTimerDisplay;